import { mdiFile, mdiImage, mdiMicrophone, mdiVideo } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Typography } from '@mui/material';
import React from 'react';
import { extractFilenameFromUrl } from '@utils/fileUtils';

// File type icons mapping
const FILE_TYPE_ICONS = {
	image: { icon: mdiImage, color: '#E53935' },
	video: { icon: mdiVideo, color: '#FF8F00' },
	audio: { icon: mdiMicrophone, color: '#1565C0' },
	default: { icon: mdiFile, color: '#128C7E' },
} as const;

// Utility function to format file size
const formatFileSize = (bytes?: number): string => {
	if (!bytes) return '';

	const units = ['B', 'KB', 'MB', 'GB'];
	let size = bytes;
	let unitIndex = 0;

	while (size >= 1024 && unitIndex < units.length - 1) {
		size /= 1024;
		unitIndex++;
	}

	return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

// Enhanced file attachment component
export const FileAttachment: React.FC<{
	src: string;
	fileName?: string;
	fileSize?: number;
	fileType?: string;
}> = ({ src, fileName, fileSize, fileType }) => {
	const getFileIcon = () => {
		if (fileType?.startsWith('image/')) return FILE_TYPE_ICONS.image;
		if (fileType?.startsWith('video/')) return FILE_TYPE_ICONS.video;
		if (fileType?.startsWith('audio/')) return FILE_TYPE_ICONS.audio;

		// Fallback to filename extension detection
		if (fileName) {
			const extension = fileName.split('.')?.pop()?.toLowerCase();
			if (extension) {
				const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
				const videoExtensions = ['mp4', 'webm', 'mkv', 'avi', 'mov', 'wmv'];
				const audioExtensions = ['mp3', 'wav', 'ogg', 'flac', 'm4a'];

				if (imageExtensions.includes(extension)) return FILE_TYPE_ICONS.image;
				if (videoExtensions.includes(extension)) return FILE_TYPE_ICONS.video;
				if (audioExtensions.includes(extension)) return FILE_TYPE_ICONS.audio;
			}
		}

		return FILE_TYPE_ICONS.default;
	};

	const { icon, color } = getFileIcon();

	const handleClick = () => {
		window.open(src, '_blank');
	};

	// Get display filename - use fileName if available, otherwise extract from URL
	const displayFileName = fileName || extractFilenameFromUrl(src);

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				padding: '12px 16px',
				backgroundColor: '#f8f9fa',
				borderRadius: '12px',
				gap: 2,
				cursor: 'pointer',
				transition: 'all 0.2s ease',
				border: '1px solid rgba(0,0,0,0.08)',
				'&:hover': {
					backgroundColor: '#e9ecef',
					transform: 'translateY(-1px)',
					boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
				},
				maxWidth: '280px',
				minWidth: '200px',
			}}
			onClick={handleClick}
		>
			{/* File icon */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					backgroundColor: 'white',
					width: 44,
					height: 44,
					borderRadius: '50%',
					boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
					flexShrink: 0,
				}}
			>
				<Icon path={icon} size={1.2} color={color} />
			</Box>

			{/* File info */}
			<Box sx={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
				<Typography
					sx={{
						fontWeight: 600,
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						fontSize: '14px',
						color: '#212529',
						lineHeight: 1.2,
					}}
				>
					{displayFileName}
				</Typography>

				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
					{fileSize && (
						<Typography
							variant="caption"
							sx={{
								color: '#6c757d',
								fontSize: '12px',
								fontWeight: 500,
							}}
						>
							{formatFileSize(fileSize)}
						</Typography>
					)}

					{fileType && (
						<>
							{fileSize && (
								<Box
									sx={{
										width: 2,
										height: 2,
										borderRadius: '50%',
										backgroundColor: '#6c757d',
									}}
								/>
							)}
							<Typography
								variant="caption"
								sx={{
									color: '#6c757d',
									fontSize: '12px',
									textTransform: 'uppercase',
									fontWeight: 500,
								}}
							>
								{fileType.split('/')[1] || fileType}
							</Typography>
						</>
					)}
				</Box>
			</Box>

			{/* Download indicator */}
			<Box
				sx={{
					width: 8,
					height: 8,
					borderRadius: '50%',
					backgroundColor: color,
					opacity: 0.6,
					flexShrink: 0,
				}}
			/>
		</Box>
	);
};
